import React, { useState, useEffect } from 'react';
import { getToken } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAudioAnalysis } from '../../contexts/AudioAnalysisContext';

const AudioAnalysis = () => {
  const { user } = useAuth();
  const isAdmin = user?.tier === 'admin';
  const {
    pendingAnalysis,
    completedAnalysis,
    analysisError,
    uploadProgress: contextUploadProgress,
    isAnalyzing,
    startAnalysis,
    cancelAnalysis,
    clearCompletedAnalysis,
    clearError
  } = useAudioAnalysis();

  const [file, setFile] = useState(null);
  const [showAllUsers, setShowAllUsers] = useState(false); // Admin: show all users' history
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, title
  const [passwordModal, setPasswordModal] = useState({ show: false, itemId: null, mode: 'unlock' }); // mode: unlock, set, change
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [speakerNames, setSpeakerNames] = useState({}); // { '화자1': '홍길동', '화자2': '김철수' }
  const [editingSpeaker, setEditingSpeaker] = useState(null);

  const ACCEPTED_FORMATS = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/webm', 'audio/ogg', 'audio/aac', 'video/mp4'];
  const ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.webm', '.ogg', '.aac', '.mp4'];
  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type (check MIME type or file extension)
    const fileExt = '.' + selectedFile.name.split('.').pop().toLowerCase();
    const isValidType = ACCEPTED_FORMATS.includes(selectedFile.type) || ACCEPTED_EXTENSIONS.includes(fileExt);

    if (!isValidType) {
      setError('지원하지 않는 파일 형식입니다. mp3, wav, m4a, webm, ogg 파일만 업로드 가능합니다.');
      setFile(null);
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('파일 크기가 너무 큽니다. 최대 100MB까지 업로드 가능합니다.');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResults(null);
  };

  // Fetch analysis history on component mount or when showAllUsers changes
  useEffect(() => {
    fetchHistory();
  }, [showAllUsers]);

  // Handle completed analysis from context (background analysis)
  useEffect(() => {
    if (completedAnalysis) {
      setResults(completedAnalysis);
      setShowTranscript(false);
      clearCompletedAnalysis();
      fetchHistory(); // Refresh history
      // Clear file after completion
      setFile(null);
    }
  }, [completedAnalysis, clearCompletedAnalysis]);

  // Handle analysis error from context
  useEffect(() => {
    if (analysisError) {
      setError(analysisError);
      clearError();
    }
  }, [analysisError, clearError]);

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const token = getToken();
      const endpoint = showAllUsers && isAdmin
        ? 'https://api.ilouli.com/api/audio/admin/history'
        : 'https://api.ilouli.com/api/audio/history';

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setHistory(data.data || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleHistoryItemClick = (item) => {
    // Check if item has password protection
    if (item.hasPassword) {
      setPasswordModal({ show: true, itemId: item.id, mode: 'unlock' });
      return;
    }

    setResults(item.results);
    setSelectedItem(item);
    setShowTranscript(false);
    // Scroll to results
    setTimeout(() => {
      document.querySelector('.results-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  const handlePasswordSubmit = async () => {
    const { itemId, mode } = passwordModal;

    // Validate password (4 digits)
    if (!/^\d{4}$/.test(passwordInput)) {
      alert('4자리 숫자를 입력해주세요.');
      return;
    }

    // Validate password confirmation for set/change modes
    if (mode !== 'unlock') {
      if (passwordInput !== passwordConfirm) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }
    }

    try {
      const token = getToken();

      if (mode === 'unlock') {
        // Verify password via GET with query parameter
        const response = await fetch(`https://api.ilouli.com/api/audio/history/${itemId}?password=${passwordInput}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const item = history.find(h => h.id === itemId);
          setResults(item.results);
          setSelectedItem(item);
          setShowTranscript(false);
          setPasswordModal({ show: false, itemId: null, mode: 'unlock' });
          setPasswordInput('');

          // Scroll to results
          setTimeout(() => {
            document.querySelector('.results-container')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 100);
        } else if (response.status === 403) {
          alert('비밀번호가 일치하지 않습니다.');
        } else {
          alert('비밀번호 확인에 실패했습니다.');
        }
      } else {
        // Set or change password via PUT
        const response = await fetch(`https://api.ilouli.com/api/audio/history/${itemId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password: passwordInput })
        });

        if (response.ok) {
          // Update local history state
          setHistory(prev => prev.map(item =>
            item.id === itemId ? { ...item, hasPassword: true } : item
          ));
          // Update selected item if it's the one being edited
          if (selectedItem && selectedItem.id === itemId) {
            setSelectedItem(prev => ({ ...prev, hasPassword: true }));
          }
          setPasswordModal({ show: false, itemId: null, mode: 'unlock' });
          setPasswordInput('');
          setPasswordConfirm('');
          alert(mode === 'set' ? '비밀번호가 설정되었습니다.' : '비밀번호가 변경되었습니다.');
        } else {
          alert('비밀번호 설정에 실패했습니다.');
        }
      }
    } catch (err) {
      console.error('Password operation error:', err);
      alert('오류가 발생했습니다.');
    }
  };

  const handlePasswordModalClose = () => {
    setPasswordModal({ show: false, itemId: null, mode: 'unlock' });
    setPasswordInput('');
    setPasswordConfirm('');
  };

  const handleSetPassword = (e, itemId) => {
    e.stopPropagation();
    const item = history.find(h => h.id === itemId);
    setPasswordModal({
      show: true,
      itemId,
      mode: item.hasPassword ? 'change' : 'set'
    });
  };

  const handleEditTitle = (e, item) => {
    e.stopPropagation();
    setEditingId(item.id);
    setEditingTitle(item.title || '');
  };

  const handleSaveTitle = async (e, itemId) => {
    e.stopPropagation();
    try {
      const token = getToken();
      const response = await fetch(`https://api.ilouli.com/api/audio/history/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: editingTitle })
      });

      if (response.ok) {
        // Update local history state
        setHistory(prev => prev.map(item =>
          item.id === itemId ? { ...item, title: editingTitle } : item
        ));
        // Update selected item if it's the one being edited
        if (selectedItem && selectedItem.id === itemId) {
          setSelectedItem(prev => ({ ...prev, title: editingTitle }));
        }
      }
    } catch (err) {
      console.error('Failed to save title:', err);
    } finally {
      setEditingId(null);
      setEditingTitle('');
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
    setEditingTitle('');
  };

  const handleDeleteItem = async (e, itemId) => {
    e.stopPropagation();
    if (!window.confirm('이 분석 결과를 삭제하시겠습니까?')) return;

    try {
      const token = getToken();
      const response = await fetch(`https://api.ilouli.com/api/audio/history/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setHistory(prev => prev.filter(item => item.id !== itemId));
        if (selectedItem && selectedItem.id === itemId) {
          setSelectedItem(null);
          setResults(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  // Filter and sort history
  const filteredAndSortedHistory = (() => {
    // First filter
    let filtered = history.filter(item => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      const title = (item.title || '').toLowerCase();
      const filename = (item.filename || '').toLowerCase();
      const summary = (item.results?.summary || '').toLowerCase();
      const keywords = (item.results?.keywords || []).join(' ').toLowerCase();
      return title.includes(query) || filename.includes(query) || summary.includes(query) || keywords.includes(query);
    });

    // Then sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title':
          const titleA = (a.title || a.filename || '').toLowerCase();
          const titleB = (b.title || b.filename || '').toLowerCase();
          return titleA.localeCompare(titleB);
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });
  })();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('파일을 선택해주세요.');
      return;
    }

    setError(null);

    // Use context's startAnalysis for background processing
    try {
      await startAnalysis(file);
    } catch (err) {
      // Error is handled by context and useEffect
      console.error('Audio analysis error:', err);
    }
  };

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Extract unique speakers from transcript
  const getUniqueSpeakers = () => {
    if (!results?.transcript || !Array.isArray(results.transcript)) return [];
    const speakers = new Set();
    results.transcript.forEach(item => {
      if (item.speaker) speakers.add(item.speaker);
    });
    return Array.from(speakers);
  };

  // Get display name for speaker
  const getSpeakerDisplayName = (speaker) => {
    return speakerNames[speaker] || speaker;
  };

  // Handle speaker name change
  const handleSpeakerNameChange = (originalName, newName) => {
    setSpeakerNames(prev => ({
      ...prev,
      [originalName]: newName.trim() || originalName
    }));
    setEditingSpeaker(null);
  };

  // Reset speaker names when results change
  useEffect(() => {
    if (results) {
      setSpeakerNames({});
    }
  }, [results?.id]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="max-w-[1000px] mx-auto py-8 px-6">
      <header className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">음성 파일 분석</h1>
        <p className="text-base text-gray-500 leading-relaxed">음성 파일을 업로드하여 텍스트로 변환하고 요약을 생성합니다</p>
      </header>

      <div className="flex flex-col gap-8">
        {/* Upload Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <div className="mb-6">
            <label className="block text-base font-semibold text-gray-900 mb-2">음성 파일 업로드</label>
            <div className="relative">
              <input
                type="file"
                accept=".mp3,.wav,.m4a,.aac,.webm,.ogg,.mp4,audio/*,video/mp4"
                onChange={handleFileChange}
                disabled={isAnalyzing}
                className="absolute opacity-0 w-0 h-0"
                id="audioFile"
              />
              <label htmlFor="audioFile" className="flex items-center gap-4 py-4 px-6 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer transition-all min-h-[52px] hover:border-blue-500 hover:bg-gray-50 peer-focus:outline-2 peer-focus:outline-blue-500 peer-focus:outline-offset-2 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed">
                <span className="text-2xl">📁</span>
                <span className="text-base text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                  {file ? file.name : '파일 선택 (mp3, wav, m4a, webm, ogg)'}
                </span>
              </label>
            </div>
            {file && (
              <div className="mt-2 text-sm text-gray-500">
                <span className="inline-block px-2 py-1 bg-gray-100 rounded">{formatFileSize(file.size)}</span>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-base mb-6">
              <span className="text-xl">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {isAnalyzing && contextUploadProgress > 0 && contextUploadProgress < 100 && (
            <div className="mb-6">
              <div className="text-sm font-medium text-gray-500 mb-2">업로드 중... {contextUploadProgress}%</div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all" style={{ width: `${contextUploadProgress}%` }}></div>
              </div>
            </div>
          )}

          {isAnalyzing && pendingAnalysis?.status === 'analyzing' && (
            <div className="flex flex-col items-center gap-2 p-6 bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-300 rounded-lg text-base text-gray-500 mb-6">
              <span className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
              <span className="text-center leading-snug">음성 파일 분석 중... (다른 페이지로 이동해도 분석이 계속됩니다)</span>
              <button
                type="button"
                className="mt-2 px-6 py-1 bg-transparent border border-gray-300 rounded text-sm text-gray-500 cursor-pointer transition-all hover:bg-red-500 hover:border-red-500 hover:text-white"
                onClick={cancelAnalysis}
              >
                분석 취소
              </button>
            </div>
          )}

          <button
            type="submit"
            className="w-full h-11 px-4 bg-blue-500 text-white text-base font-semibold border-none rounded-lg cursor-pointer transition-all hover:bg-blue-600 hover:-translate-y-px hover:shadow-md active:bg-blue-700 active:translate-y-0 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            disabled={!file || isAnalyzing}
          >
            {isAnalyzing ? '분석 중...' : '분석 시작'}
          </button>
        </form>

        {/* Results */}
        {results && (
          <div className="results-container flex flex-col gap-8">
            <div className="mb-6">
              <div className="flex flex-col gap-2">
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  {selectedItem?.title || selectedItem?.filename || '분석 결과'}
                </h2>
                {selectedItem && (
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm text-gray-500">{formatDate(selectedItem.createdAt)}</span>
                    <button
                      className="px-4 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-medium text-gray-500 cursor-pointer transition-all flex items-center gap-1 hover:bg-amber-500/10 hover:border-amber-500 hover:text-amber-500"
                      onClick={() => {
                        const item = history.find(h => h.id === selectedItem.id);
                        setPasswordModal({
                          show: true,
                          itemId: selectedItem.id,
                          mode: item?.hasPassword ? 'change' : 'set'
                        });
                      }}
                      title={selectedItem.hasPassword ? '비밀번호 변경' : '비밀번호 설정'}
                    >
                      {selectedItem.hasPassword ? '🔒 비밀번호 변경' : '🔓 비밀번호 설정'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Keywords Section - moved to top */}
            {results.keywords && results.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 p-6 bg-white border border-gray-200 rounded-2xl mb-8">
                {results.keywords.map((keyword, index) => (
                  <span key={index} className="inline-block px-4 py-1 bg-gray-100 border border-gray-200 rounded-full text-sm font-medium text-blue-500 transition-all hover:bg-blue-500/10 hover:border-blue-500">{keyword}</span>
                ))}
              </div>
            )}

            {/* Transcript Section */}
            <section className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <div className="flex justify-between items-center mb-6 gap-4">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0">
                  <span className="text-2xl">💬</span>
                  전사 텍스트
                </h3>
                <button
                  type="button"
                  className="px-4 py-1 bg-gray-100 border border-gray-300 rounded text-sm font-medium text-blue-500 cursor-pointer transition-all whitespace-nowrap hover:bg-blue-500/10 hover:border-blue-500 active:scale-[0.98]"
                  onClick={() => setShowTranscript(!showTranscript)}
                >
                  {showTranscript ? '접기' : '전사 텍스트 보기'}
                </button>
              </div>

              {showTranscript && (
                <div className="flex flex-col gap-4 mt-4">
                  {/* Speaker List Panel */}
                  {getUniqueSpeakers().length > 0 && (
                    <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-gray-900">등장인물</span>
                        <span className="text-xs text-gray-400">클릭하여 이름 수정</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {getUniqueSpeakers().map((speaker, index) => (
                          <div key={speaker} className="flex-shrink-0">
                            {editingSpeaker === speaker ? (
                              <input
                                type="text"
                                className="w-[100px] px-2 py-1 border border-blue-500 rounded text-sm outline-none"
                                defaultValue={getSpeakerDisplayName(speaker)}
                                autoFocus
                                onBlur={(e) => handleSpeakerNameChange(speaker, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSpeakerNameChange(speaker, e.target.value);
                                  if (e.key === 'Escape') setEditingSpeaker(null);
                                }}
                                placeholder="이름 입력"
                              />
                            ) : (
                              <button
                                className="flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-500/5 group"
                                onClick={() => setEditingSpeaker(speaker)}
                              >
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}></span>
                                <span className="text-sm font-medium text-gray-900">{getSpeakerDisplayName(speaker)}</span>
                                {speakerNames[speaker] && (
                                  <span className="text-xs text-gray-400">({speaker})</span>
                                )}
                                <span className="text-xs text-gray-400 opacity-0 transition-opacity group-hover:opacity-100">✎</span>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Compact Transcript */}
                  <div className="flex flex-col gap-0 bg-gray-100 rounded-lg p-2 max-h-[350px] overflow-y-auto">
                    {results.transcript && results.transcript.length > 0 ? (
                      results.transcript.map((item, index) => {
                        const speakerIndex = getUniqueSpeakers().indexOf(item.speaker);
                        return (
                          <div key={index} className="flex items-baseline gap-2 py-1 px-2 rounded transition-colors hover:bg-white">
                            <span className="text-[11px] font-mono text-gray-400 flex-shrink-0 w-[40px]">{item.time || formatTimestamp(item.timestamp || 0)}</span>
                            <span
                              className="text-xs font-semibold flex-shrink-0 min-w-[50px] max-w-[80px] overflow-hidden text-ellipsis whitespace-nowrap"
                              style={{ color: `hsl(${speakerIndex * 60}, 70%, 40%)` }}
                            >
                              {getSpeakerDisplayName(item.speaker)}
                            </span>
                            <span className="text-sm text-gray-900 leading-snug">{item.text}</span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-base text-gray-900 leading-relaxed m-0 whitespace-pre-wrap">{results.transcript}</p>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Key Points Section */}
            {results.keyPoints && results.keyPoints.length > 0 && (
              <section className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0 mb-6">
                  <span className="text-2xl">🎯</span>
                  핵심 포인트
                </h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-4">
                  {results.keyPoints.map((point, index) => (
                    <li key={index} className="relative pl-6 text-base text-gray-900 leading-relaxed before:content-['•'] before:absolute before:left-0 before:text-blue-500 before:font-bold before:text-xl">{point}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Summary Section */}
            {results.summary && (
              <section className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0 mb-6">
                  <span className="text-2xl">📝</span>
                  요약
                </h3>
                <p className="text-base text-gray-900 leading-relaxed m-0 whitespace-pre-wrap">{results.summary}</p>
              </section>
            )}
          </div>
        )}

        {/* Analysis History - moved below */}
        {(history.length > 0 || isAdmin) && (
          <section className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
            <div className="flex justify-between items-center flex-wrap gap-4 mb-6">
              <div className="flex justify-between items-center gap-4">
                <h2 className="text-lg font-bold text-gray-900 m-0 flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
                  {showAllUsers ? '전체 분석 결과' : '분석 결과'}
                </h2>
                {isAdmin && (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showAllUsers}
                      onChange={(e) => setShowAllUsers(e.target.checked)}
                      className="w-4 h-4 cursor-pointer accent-blue-500"
                    />
                    <span className="text-sm text-gray-500 whitespace-nowrap hover:text-blue-500">전체 사용자 보기</span>
                  </label>
                )}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    className={`px-4 py-1 bg-transparent border-none rounded text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${sortBy === 'newest' ? 'bg-blue-500 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                    onClick={() => setSortBy('newest')}
                  >
                    최신순
                  </button>
                  <button
                    className={`px-4 py-1 bg-transparent border-none rounded text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${sortBy === 'oldest' ? 'bg-blue-500 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                    onClick={() => setSortBy('oldest')}
                  >
                    오래된순
                  </button>
                  <button
                    className={`px-4 py-1 bg-transparent border-none rounded text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${sortBy === 'title' ? 'bg-blue-500 text-white' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`}
                    onClick={() => setSortBy('title')}
                  >
                    제목순
                  </button>
                </div>
                <div className="flex items-center gap-1 px-4 py-1.5 bg-gray-100 border border-gray-200 rounded-full transition-all min-w-[140px] max-w-[200px] h-8 focus-within:border-blue-500 focus-within:shadow-[0_0_0_2px_rgba(59,130,246,0.15)]">
                  <span className="text-sm opacity-60">🔍</span>
                  <input
                    type="text"
                    className="flex-1 border-none bg-transparent text-sm text-gray-900 outline-none min-w-0 placeholder:text-gray-400"
                    placeholder="검색..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="w-5 h-5 flex items-center justify-center bg-gray-50 border-none rounded-full cursor-pointer text-xs text-gray-500 transition-all hover:bg-gray-300 hover:text-gray-900"
                      onClick={() => setSearchQuery('')}
                      title="검색어 지우기"
                    >✕</button>
                  )}
                </div>
              </div>
            </div>
            {isLoadingHistory ? (
              <div className="flex items-center justify-center gap-4 py-8 text-gray-500">
                <span className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></span>
                <span>불러오는 중...</span>
              </div>
            ) : filteredAndSortedHistory.length === 0 && !isAnalyzing ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500 text-center">
                <span className="text-[2rem] mb-4 opacity-50">🔎</span>
                <p className="m-0 text-base">{searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다` : '분석 결과가 없습니다'}</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
                <table className={`w-full border-collapse text-base ${showAllUsers ? 'with-user' : ''}`}>
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[60px] text-center">번호</th>
                      {showAllUsers && <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[140px] min-w-[120px]">사용자</th>}
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm">제목</th>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[120px] text-center">날짜</th>
                      <th className="p-4 text-left font-semibold text-gray-900 text-sm w-[120px] text-center">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* 분석 중인 파일 표시 */}
                    {isAnalyzing && pendingAnalysis && (
                      <tr className="border-b border-gray-200 bg-gradient-to-r from-amber-100 to-amber-200 animate-pulse">
                        <td className="p-4 text-center text-gray-500 font-medium">-</td>
                        {showAllUsers && (
                          <td className="p-4">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium text-gray-900">{user?.name || '나'}</span>
                            </div>
                          </td>
                        )}
                        <td className="p-4">
                          <div className="flex flex-row items-center gap-2">
                            <span className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin flex-shrink-0"></span>
                            <span className="font-medium text-gray-900">{pendingAnalysis.fileName}</span>
                            <span className="text-sm font-medium text-gray-500">
                              {pendingAnalysis.status === 'uploading'
                                ? `업로드 중... ${contextUploadProgress}%`
                                : '분석 진행 중...'}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-center text-gray-500 text-sm">-</td>
                        <td className="p-4 text-center">
                          <button
                            className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded cursor-pointer text-sm text-red-500 transition-all hover:bg-red-500/10 hover:border-red-500"
                            onClick={cancelAnalysis}
                            title="분석 취소"
                          >✕</button>
                        </td>
                      </tr>
                    )}
                    {filteredAndSortedHistory.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`border-b border-gray-200 cursor-pointer transition-all hover:bg-gray-100 ${selectedItem?.id === item.id ? 'bg-blue-500/[0.08] border-l-[3px] border-l-blue-500' : ''}`}
                        onClick={() => handleHistoryItemClick(item)}
                      >
                        <td className="p-4 text-center text-gray-500 font-medium">{filteredAndSortedHistory.length - index}</td>
                        {showAllUsers && (
                          <td className="p-4">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium text-gray-900">{item.userName || '알 수 없음'}</span>
                              <span className="text-xs text-gray-400">{item.userEmail}</span>
                            </div>
                          </td>
                        )}
                        <td className="p-4">
                          {editingId === item.id ? (
                            <div className="flex items-center gap-2 w-full" onClick={e => e.stopPropagation()}>
                              <input
                                type="text"
                                className="flex-1 py-2 px-4 border border-blue-500 rounded text-base bg-white text-gray-900 outline-none focus:shadow-[0_0_0_2px_rgba(59,130,246,0.2)]"
                                value={editingTitle}
                                onChange={(e) => setEditingTitle(e.target.value)}
                                placeholder="제목을 입력하세요"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveTitle(e, item.id);
                                  if (e.key === 'Escape') handleCancelEdit(e);
                                }}
                              />
                              <button
                                className="w-8 h-8 flex items-center justify-center border-none rounded cursor-pointer text-base font-bold transition-all bg-blue-500 text-white hover:bg-blue-600"
                                onClick={(e) => handleSaveTitle(e, item.id)}
                                title="저장"
                              >✓</button>
                              <button
                                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded cursor-pointer text-base font-bold transition-all bg-gray-100 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                                onClick={handleCancelEdit}
                                title="취소"
                              >✕</button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1 items-start">
                              <span className="flex items-center gap-1 font-medium text-gray-900">
                                {item.title || item.filename}
                                {item.hasPassword && <span className="text-sm opacity-70">🔒</span>}
                              </span>
                              {item.results?.keywords && item.results.keywords.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {item.results.keywords.slice(0, 3).map((keyword, idx) => (
                                    <span key={idx} className="inline-block px-1 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-[11px] text-blue-500 font-medium">{keyword}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-center text-gray-500 text-sm">{formatDate(item.createdAt)}</td>
                        <td className="p-4 text-center" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <button
                              className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded cursor-pointer text-sm text-gray-500 transition-all relative hover:bg-blue-500/10 hover:border-blue-500 hover:text-blue-500"
                              onClick={(e) => handleEditTitle(e, item)}
                              title="제목 수정"
                            >✎</button>
                            <button
                              className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded cursor-pointer text-sm text-gray-500 transition-all relative hover:bg-amber-500/10 hover:border-amber-500"
                              onClick={(e) => handleSetPassword(e, item.id)}
                              title={item.hasPassword ? '비밀번호 변경' : '비밀번호 설정'}
                            >
                              {item.hasPassword ? '🔒' : '🔓'}
                            </button>
                            <button
                              className="w-7 h-7 flex items-center justify-center bg-white border border-gray-200 rounded cursor-pointer text-sm text-gray-500 transition-all relative hover:bg-red-500/10 hover:border-red-500"
                              onClick={(e) => handleDeleteItem(e, item.id)}
                              title="삭제"
                            >🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Password Modal */}
        {passwordModal.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-6" onClick={handlePasswordModalClose}>
            <div className="bg-white rounded-2xl p-8 max-w-[400px] w-full shadow-2xl" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-gray-900 m-0 mb-2">
                {passwordModal.mode === 'unlock' && '비밀번호 입력'}
                {passwordModal.mode === 'set' && '비밀번호 설정'}
                {passwordModal.mode === 'change' && '비밀번호 변경'}
              </h3>
              <p className="text-base text-gray-500 m-0 mb-6 leading-relaxed">
                {passwordModal.mode === 'unlock'
                  ? '이 항목을 보려면 비밀번호를 입력하세요.'
                  : '4자리 숫자를 입력하세요.'}
              </p>
              <input
                type="password"
                className="w-full p-4 border border-gray-300 rounded-lg text-base text-center tracking-[8px] font-bold bg-gray-100 text-gray-900 outline-none transition-all mb-6 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] placeholder:tracking-normal placeholder:text-gray-400"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder={passwordModal.mode === 'unlock' ? '4자리 숫자' : '비밀번호 (4자리 숫자)'}
                maxLength={4}
                pattern="\d{4}"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && passwordModal.mode === 'unlock') handlePasswordSubmit();
                  if (e.key === 'Escape') handlePasswordModalClose();
                }}
              />
              {passwordModal.mode !== 'unlock' && (
                <input
                  type="password"
                  className="w-full p-4 border border-gray-300 rounded-lg text-base text-center tracking-[8px] font-bold bg-gray-100 text-gray-900 outline-none transition-all mb-6 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] placeholder:tracking-normal placeholder:text-gray-400"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="비밀번호 확인"
                  maxLength={4}
                  pattern="\d{4}"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handlePasswordSubmit();
                    if (e.key === 'Escape') handlePasswordModalClose();
                  }}
                />
              )}
              <div className="flex gap-4 justify-end">
                <button className="px-8 py-2 rounded-lg text-base font-semibold cursor-pointer transition-all min-w-[80px] h-11 bg-gray-100 text-gray-500 border border-gray-300 hover:bg-gray-50 hover:text-gray-900" onClick={handlePasswordModalClose}>
                  취소
                </button>
                <button className="px-8 py-2 rounded-lg text-base font-semibold cursor-pointer transition-all min-w-[80px] h-11 bg-blue-500 text-white border-none hover:bg-blue-600 hover:-translate-y-px hover:shadow-md active:translate-y-0" onClick={handlePasswordSubmit}>
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioAnalysis;
